import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    ClientDataServiceName,
    ClientEndpoints,
    IClientDataService,
} from '../../../DataServices/ClientDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { ApiDataResponse, BaseApiDataResponse } from '../../../Models/ApiData';
import {
    Client,
    ClientDetailsApiDataResponse,
    ClientListOrder,
    ClientListResponse,
    ClientSummaryListResponse,
} from '../../../Models/Clients/Client';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface IClientService {
    listSummaryClient: (
        params: { search?: string; showOnlyActive?: boolean },
        cts: XHRRequestCanceler
    ) => Promise<ClientSummaryListResponse>;

    listClient: (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: ClientListOrder;
            showOnlyActive?: boolean;
        }
    ) => Promise<ClientListResponse>;

    getClient: (cts: XHRRequestCanceler, clientId: string) => Promise<ClientDetailsApiDataResponse>;

    updateClient: (
        clientId: string,
        client: Client,
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    createClient: (client: Client, cts: XHRRequestCanceler) => Promise<ApiDataResponse<string>>;

    exportClientList: (cts: XHRRequestCanceler, search: string, order?: ClientListOrder) => void;
}

class ClientService extends BaseReduxService implements IClientService {
    private readonly clientDataService: Lazy<IClientDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly downloadService: Lazy<IDownloadService>;
    private readonly authService: Lazy<IAuthService>;

    constructor() {
        super();
        this.clientDataService = IoC.getLazy<IClientDataService>(ClientDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
    }

    public listSummaryClient = (
        params: { search?: string; showOnlyActive?: boolean },
        cts: XHRRequestCanceler
    ): Promise<ClientSummaryListResponse> => {
        try {
            return this.clientDataService.value().listSummaryClient(params, cts);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public listClient = async (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: ClientListOrder;
            showOnlyActive?: boolean;
        }
    ) => {
        try {
            return this.clientDataService.value().listClient(
                {
                    search: params.search,
                    order: params.order,
                    showOnlyActive: params.showOnlyActive,
                },
                cts
            );
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getClient = (cts: XHRRequestCanceler, clientId: string) => {
        try {
            return this.clientDataService.value().getClient(cts, clientId);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public updateClient = async (clientId: string, client: Client, cts: XHRRequestCanceler) => {
        try {
            const response = await this.clientDataService
                .value()
                .updateClient(clientId, client, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public createClient = async (
        client: Client,
        cts: XHRRequestCanceler
    ): Promise<ApiDataResponse<string>> => {
        try {
            const response = await this.clientDataService.value().createClient(client, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            this.toastApiError(this.i18nService.value().getString(`Error_${err.error_code}` || ''));
            return Promise.reject(err);
        }
    };

    public exportClientList = async (
        cts: XHRRequestCanceler,
        search: string,
        order?: ClientListOrder | undefined
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            params['search'] = search;
            if (order) {
                params['order'] = order.valueOf();
            }
            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: ClientEndpoints.EXPORT_CLIENT_LIST,
                    auth: await this.authService.value().getAuthorization(),
                },
                params
            );
            this.downloadService.value().downloadFile(url, 'Clients', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const ClientServiceName = 'ClientService';
export { ClientService, ClientServiceName };
